import styles from './LicenseManager.module.css';

import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack } from "@mui/material";
import dayjs from "dayjs";
import { useFormik } from "formik";
import React from "react";
import { FaRegCalendarCheck, FaRegCalendarTimes } from 'react-icons/fa';
import { MdPeople } from 'react-icons/md';
import { TbLicense, TbLicenseOff } from 'react-icons/tb';
import * as Yup from 'yup';
import { VdsButton, VdsTextField } from "../../../@vds";
import { EmptyState } from '../../../components/EmptyState/EmptyState';
import { ENABLE_LICENSE_MANAGEMENT } from '../../../constants/appRoutes';
import { useAlertContext, useLoaderContext } from '../../../contexts';
import { UpsertLicenses } from "../../../helpers/api.helpers";

type LicenseManagerComponent = React.FC<{
    license: {
        oid: string
        companyOid: string
        code: string
        type: string
        description: string
        slots: number
        validityStartDate: string
        validityEndDate: string
        creationDate: string
        lastUpdateDate: string
        enabled: boolean
    } | null
    activation: {
        id: string
        url: string
        serverOid: string
        serverUri: string
        serverDomain: string
        licenseOid: string
        licenseSlots: number
        companyUsers: number
        roleOid: string
        roleName: string
    }
    company: { name: string; id: string; }
    onReload: () => void;
}>;

export const LicenseManager: LicenseManagerComponent = (props) => {

    const {
        company,
        license,
        activation,
        onReload
    } = props;

    const setLoader = useLoaderContext();
    const setAlert = useAlertContext();

    const [licenseDialog, setLicenseDialog] = React.useState(false);

    const licenseFormik = useFormik({
        initialValues: {
            code: '',
            licenseSlots: 0,
            validityStartDate: '',
            validityEndDate: '',
            description: '',
            type: ''
        },
        validationSchema: Yup.object().shape({
            code: Yup
                .string()
                .required("Campo obbligatorio"),
            type: Yup
                .string()
                .required("Campo obbligatorio"),
            licenseSlots: Yup
                .number()
                .min(1, "Inserire un valore maggiore di zero")
                .typeError("Inserire un numero valido")
                .required("Campo obbligatorio"),
            validityStartDate: Yup
                .string()
                .required("Campo obbligatorio")
                .test(
                    'is-date',
                    'Formato data non valido',
                    (value) => {
                        return dayjs(value, 'YYYY-MM-DD', true).isValid();
                    }
                )
                .test(
                    'start-date-valid',
                    'Inserire un range di date valido',
                    function (value) {
                        const startDate = dayjs(value, 'YYYY-MM-DD');
                        const endDate = dayjs(this.parent.validityEndDate, 'YYYY-MM-DD');
                        if (!startDate.isValid() || !endDate.isValid()) {
                            return true;
                        }
                        return startDate.isSame(endDate, 'day') || startDate.isBefore(endDate, 'day');
                    }
                ),
            validityEndDate: Yup
                .string()
                .required("Campo obbligatorio")
                .test(
                    'is-date',
                    'Formato data non valido',
                    (value) => {
                        return dayjs(value, 'YYYY-MM-DD', true).isValid();
                    }
                )
                .test(
                    'end-date-valid',
                    'Inserire un range di date valido',
                    function (value) {
                        const startDate = dayjs(this.parent.validityStartDate, 'YYYY-MM-DD');
                        const endDate = dayjs(value, 'YYYY-MM-DD');
                        if (!startDate.isValid() || !endDate.isValid()) {
                            return true;
                        }
                        return endDate.isSame(startDate, 'day') || endDate.isAfter(startDate, 'day');
                    }
                ),
        }),
        onSubmit: async (values) => {

            try {
                await licenseFormik.validateForm(values);
            } catch (validationErrors) {
                console.error("Errore di validazione prima del submit:", validationErrors);
                return;
            }

            const dataToSend = {
                ...values,
                slots: values.licenseSlots,
                validityStartDate: values.validityStartDate ? dayjs(values.validityStartDate).toISOString() : '',
                validityEndDate: values.validityEndDate ? dayjs(values.validityEndDate).toISOString() : '',
            };

            setLoader(true);
            UpsertLicenses({
                params: { companyOid: company.id },
                data: dataToSend
            }).then(({ data }) => {
                setLoader(false);
                setLicenseDialog(false);
                onReload();
                setAlert({
                    open: true,
                    severity: 'success',
                    message: license ?
                        "Licenza modificata con successo" :
                        "Licenza aggiunta con successo"
                });
            }).catch((error) => {
                setLoader(false);
                const errors = {
                    '403.3004': 'Non ci sono slot disponibili',
                    '400.4025': 'Non ci sono slot disponibili',
                }
                setAlert({
                    message: errors[error?.response?.data?.title] || 'Si è verificato un errore imprevisto si prega di riprovare',
                    severity: 'warning',
                    open: true,
                });
            });
        }
    });

    const handleDateChangeAndValidate = async (fieldName: 'validityStartDate' | 'validityEndDate', value: string) => {
        await licenseFormik.setFieldValue(fieldName, value);
        await licenseFormik.setFieldTouched(fieldName, true);
        await licenseFormik.validateField('validityStartDate');
        await licenseFormik.validateField('validityEndDate');
    };

    const dateNow = dayjs();
    const isFutureLicense = license?.validityStartDate && dayjs(license.validityStartDate).isAfter(dateNow);
    const isActive = license?.validityEndDate && dayjs(license.validityEndDate).isAfter(dayjs()) && dayjs(license.validityStartDate).isBefore(dayjs());

    const createNewLicense = () => {
        setLicenseDialog(true);
        licenseFormik.resetForm();
        licenseFormik.setTouched({});
    };

    return (
        <>
            <Grid container spacing={2} pt={2}>
                {ENABLE_LICENSE_MANAGEMENT && license && (
                    <Grid xs={12} item>
                        <Stack display="flex" flexDirection="row" justifyContent="end">
                            <VdsButton
                                color={isActive ? "primary" : "secondary"}
                                variant="contained"
                                onClick={createNewLicense}
                            >
                                {(isActive || isFutureLicense) ? "Modifica licenza" : "Rinnova licenza"}
                            </VdsButton>
                        </Stack>
                    </Grid>
                )}
                {license ? (
                    <Grid xs={12} item>
                        <Box className={styles.licenseBox}>
                            <Box display="flex" flexDirection="column" gap="16px" p="24px" flexGrow={1}>
                                <Stack display="flex" flexDirection="row" justifyContent="space-between">
                                    <Box className={styles.licenseTitle}>
                                        CD-Key: {license.code}
                                    </Box>
                                    {isActive ? (
                                        <Box>
                                            <Chip label="Attiva" className={styles.chipActive} />
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Chip label={isFutureLicense ? "Non Attiva" : "Scaduta"} className={styles.chipExpired} />
                                        </Box>
                                    )}
                                </Stack>
                                <Stack display="flex" gap="8px" flexDirection="row" alignItems="center">
                                    <Box>
                                        {isActive ? <FaRegCalendarCheck /> : <FaRegCalendarTimes />}
                                    </Box>
                                    <Box className={styles.label}>
                                        Valida dal:
                                    </Box>
                                    <Box className={styles.value}>
                                        {dayjs(license.validityStartDate).format('DD/MM/YYYY')}
                                    </Box>
                                    <Box className={styles.label}>
                                        al:
                                    </Box>
                                    <Box className={styles.value}>
                                        {dayjs(license.validityEndDate).format('DD/MM/YYYY')}
                                    </Box>
                                </Stack>
                                <Stack display="flex" gap="8px" flexDirection="row" alignItems="center">
                                    <Box>
                                        <MdPeople />
                                    </Box>
                                    <Box className={styles.label}>
                                        Slots disponibili:
                                    </Box>
                                    <Box className={styles.value}>
                                        {activation.companyUsers}/{activation.licenseSlots}
                                    </Box>
                                </Stack>
                                <Stack display="flex" gap="8px" flexDirection="row" alignItems="center">
                                    <Box>
                                        <TbLicense />
                                    </Box>
                                    <Box className={styles.label}>
                                        Tipo:
                                    </Box>
                                    <Box className={styles.value}>
                                        {license.type}
                                    </Box>
                                </Stack>
                            </Box>
                        </Box>
                    </Grid>
                ) : (
                    <Grid xs={12} item>
                        <EmptyState
                            title="Non ci sono licenze valide per l'azienda"
                            icon={<TbLicenseOff />}
                            action={ENABLE_LICENSE_MANAGEMENT ? {
                                text: "Aggiungi licenza",
                                onClick: createNewLicense,
                            } : null}
                        />
                    </Grid>
                )}
            </Grid>

            <Dialog
                open={licenseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Aggiungi licenza</DialogTitle>
                <DialogContent>
                    <Grid spacing={2} container component="form" autoComplete='off' spellCheck="false">
                        <Grid xs={12} item>
                            <VdsTextField
                                label="CD-Key*"
                                name="code"
                                onChange={licenseFormik.handleChange}
                                value={licenseFormik.values.code}
                                onBlur={licenseFormik.handleBlur}
                                error={Boolean(licenseFormik.touched.code && licenseFormik.errors.code)}
                                errorText={licenseFormik.touched.code && licenseFormik.errors.code}
                                collapse
                            />
                        </Grid>
                        <Grid xs={6} item>
                            <VdsTextField
                                type="date"
                                inputProps={{ max: licenseFormik.values.validityEndDate }}
                                name="validityStartDate"
                                label="Valida da*"
                                onChange={(ev) => handleDateChangeAndValidate("validityStartDate", ev.target.value)}
                                onBlur={licenseFormik.handleBlur}
                                value={licenseFormik.values.validityStartDate}
                                error={Boolean(licenseFormik.touched.validityStartDate && licenseFormik.errors.validityStartDate)}
                                errorText={licenseFormik.touched.validityStartDate && licenseFormik.errors.validityStartDate}
                                collapse
                            />
                        </Grid>
                        <Grid xs={6} item>
                            <VdsTextField
                                type="date"
                                inputProps={{ min: licenseFormik.values.validityStartDate }}
                                name="validityEndDate"
                                label="fino al*"
                                onChange={(ev) => handleDateChangeAndValidate("validityEndDate", ev.target.value)}
                                onBlur={licenseFormik.handleBlur}
                                value={licenseFormik.values.validityEndDate}
                                error={Boolean(licenseFormik.touched.validityEndDate && licenseFormik.errors.validityEndDate)}
                                errorText={licenseFormik.touched.validityEndDate && licenseFormik.errors.validityEndDate}
                                collapse
                            />
                        </Grid>
                        <Grid xs={6} item>
                            <VdsTextField
                                label="Slot disponibili*"
                                name="licenseSlots"
                                onChange={licenseFormik.handleChange}
                                value={licenseFormik.values.licenseSlots}
                                onBlur={licenseFormik.handleBlur}
                                error={Boolean(licenseFormik.touched.licenseSlots && licenseFormik.errors.licenseSlots)}
                                errorText={licenseFormik.touched.licenseSlots && licenseFormik.errors.licenseSlots}
                                collapse
                            />
                        </Grid>
                        <Grid xs={6} item>
                            <VdsTextField
                                label="Tipo*"
                                name="type"
                                onChange={licenseFormik.handleChange}
                                value={licenseFormik.values.type}
                                onBlur={licenseFormik.handleBlur}
                                error={Boolean(licenseFormik.touched.type && licenseFormik.errors.type)}
                                errorText={licenseFormik.touched.type && licenseFormik.errors.type}
                                collapse
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setLicenseDialog(false);
                        }}
                    >
                        Annulla
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => licenseFormik.handleSubmit()}
                    >
                        Salva
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}